import { AppId, FCX, ProductLogo, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import Page from "@containers/Page/Page";
import PageContent from "@containers/PageContent/PageContent";
import FoodSummary from "@pages/Food/components/Summary/Summary";
import './Food.scss';
import { useFoodProfileSummary } from "@apiHooks/useFoodProfileSummary";
import useQueryParam from "@hooks/useQueryParam";
import FoodSectionMenu from "@pages/Food/components/SectionMenu/SectionMenu";
import FoodSectionConsumer from "@pages/Food/components/SectionConsumer/SectionConsumer";
import SectionsDocuments from "@pages/Food/components/SectionsDocuments/SectionsDocuments";
import FoodSectionLaunches from "@pages/Food/components/SectionLaunches/SectionLaunches";
import FoodSectionsNav from "@pages/Food/components/SectionsNav/SectionsNav";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";
import { useMemo, useState } from "react";
import ScrollTopButton from "@pages/Food/components/ScrollToTop/ScrollToTop";
import SectionSocial from "@pages/Food/components/SectionSocial/SectionSocial";

export enum FoodSectionEnum {
    Summary = 'FoodSection--Summary',
    MenuAndConsumer = 'FoodSection--MenuAndConsumer',
    Social = 'FoodSection--Social',
    Charts = 'FoodSection--Charts',
    Reports = 'FoodSection--Reports',
    Launches = 'FoodSection--Launches',
}

export const FOOD_PAGE_SCROLLBAR_ID = 'FoodPageScrollbar';

const Food: FCX = () => {
    const [hasSocial, setHasSocial] = useState(false);
    const [hasReports, setHasReports] = useState(false);
    const [hasCharts, setHasCharts] = useState(false);
    const [hasLaunches, setHasLaunches] = useState(false);

    const isSocialEnabled= usePlatformFeatureFlag('SNAP Food Profile Social Listening');

    const word = useQueryParam('q');
    const {
        data: {
            summary,
            menu,
            consumer,
        },
        isLoading,
    } = useFoodProfileSummary(word);

    const hasConsumer = useMemo(
        () => consumer.hasData,
        [consumer.hasData]
    );

    const navItems = useMemo(
        () => {
            const result = [
                {
                    id: FoodSectionEnum.MenuAndConsumer,
                    title: hasConsumer ? 'Menu and Consumer' : 'Menu',
                }
            ]

            if (isSocialEnabled && hasSocial) result.push({id: FoodSectionEnum.Social, title: 'Social'});
            if (hasReports) result.push({id: FoodSectionEnum.Reports, title: 'Reports'});
            if (hasCharts) result.push({id: FoodSectionEnum.Charts, title: 'Charts'});
            if (hasLaunches) result.push({id: FoodSectionEnum.Launches, title: 'Launches'});

            return result;
        },
        [hasConsumer, hasReports, hasCharts, hasLaunches, hasSocial]
    );

    return (
        /* @ts-ignore */
        <Page>
            <PageContent>
                <CustomBaseScrollbars id={FOOD_PAGE_SCROLLBAR_ID}>
                    <div className="Food">
                        <div className="Food__wrapper">
                            {isLoading && (
                                <div className="spinner spinner-blink">
                                    <ProductLogo appId={AppId.Snap} type="regular"/>
                                </div>
                            )}
                            {!isLoading && (<>
                                <FoodSummary data={summary}/>
                                <FoodSectionsNav
                                    word={summary.name}
                                    data={navItems}
                                />
                                <div
                                    className="Food__section"
                                    id={FoodSectionEnum.MenuAndConsumer}
                                    data-testid="blockFoodSectionMenuAndConsumer"
                                >
                                    <FoodSectionMenu data={menu}/>
                                    {consumer.hasData && (
                                        <FoodSectionConsumer data={consumer}/>
                                    )}
                                </div>
                                {isSocialEnabled && !!summary.id && (
                                    <SectionSocial
                                        setHasData={setHasSocial}
                                        word={summary.name}
                                        wordId={summary.id}
                                    />
                                )}
                                <SectionsDocuments
                                    word={summary.name}
                                    setHasReports={setHasReports}
                                    setHasCharts={setHasCharts}
                                />
                                <FoodSectionLaunches
                                    word={summary.name}
                                    setHasData={setHasLaunches}
                                />
                            </>)}
                        </div>
                    </div>
                </CustomBaseScrollbars>
                <ScrollTopButton
                    scrollElementId={FOOD_PAGE_SCROLLBAR_ID}
                    testId="buttonFoodScrollToTop"
                />
            </PageContent>
        </Page>
    );
}

export default Food;